import jsPDF from 'jspdf';
import { LatLngBounds } from 'leaflet';
import { PaperMapTemplate, TemplateElement } from './paperMapTemplate';
import { MapScale, SCALE_FACTORS } from './exportTypes';

export interface RenderOptions {
    template: PaperMapTemplate;
    scale: MapScale;
    viewportBounds: LatLngBounds;
    mapImageDataUrl: string;
    title?: string;
}

/**
 * Renders a paper map PDF using the provided template and map image.
 */
export function renderPaperMapPdf(options: RenderOptions): jsPDF {
    const { template, scale, mapImageDataUrl } = options;

    // Create PDF with page dimensions from template
    const pdf = new jsPDF({
        orientation: template.pageSize.width > template.pageSize.height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [template.pageSize.width, template.pageSize.height],
    });

    // Draw page border (print margins indicator)
    drawPrintMarginBorder(pdf, template);

    // Place the map image in the viewport area
    pdf.addImage(
        mapImageDataUrl,
        'JPEG',
        template.mapViewport.x,
        template.mapViewport.y,
        template.mapViewport.width,
        template.mapViewport.height
    );

    // Draw viewport border
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.rect(
        template.mapViewport.x,
        template.mapViewport.y,
        template.mapViewport.width,
        template.mapViewport.height
    );

    // Render template elements
    template.elements.forEach(element => {
        renderTemplateElement(pdf, element, scale, options.title);
    });

    return pdf;
}

function drawPrintMarginBorder(pdf: jsPDF, template: PaperMapTemplate): void {
    // Light gray dashed line showing print margins
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.1);

    const m = template.printMargins;
    const p = template.pageSize;

    // Draw dashed rectangle at print margins
    pdf.setLineDashPattern([2, 2], 0);
    pdf.rect(m.left, m.top, p.width - m.left - m.right, p.height - m.top - m.bottom);
    pdf.setLineDashPattern([], 0);
}

function renderTemplateElement(
    pdf: jsPDF,
    element: TemplateElement,
    scale: MapScale,
    title?: string
): void {
    switch (element.type) {
        case 'rect':
            renderRect(pdf, element);
            break;
        case 'text':
            renderText(pdf, element, title);
            break;
        case 'scaleBar':
            renderScaleBar(pdf, element, scale);
            break;
        case 'northArrow':
            renderNorthArrow(pdf, element);
            break;
        case 'line':
            renderLine(pdf, element);
            break;
    }
}

function renderRect(pdf: jsPDF, element: TemplateElement): void {
    const style = element.style || {};

    if (style.fillColor) {
        pdf.setFillColor(style.fillColor);
    }
    if (style.strokeColor) {
        pdf.setDrawColor(style.strokeColor);
    }
    pdf.setLineWidth(style.strokeWidth || 0.5);

    const drawStyle = style.fillColor && style.strokeColor ? 'FD' :
        style.fillColor ? 'F' : 'S';

    pdf.rect(element.rect.x, element.rect.y, element.rect.width, element.rect.height, drawStyle);
}

function renderText(pdf: jsPDF, element: TemplateElement, title?: string): void {
    const style = element.style || {};
    const text = element.content || title || '';

    pdf.setFontSize(style.fontSize || 10);
    pdf.setTextColor(0, 0, 0);

    const align = style.textAlign || 'left';
    pdf.text(text, element.rect.x, element.rect.y, { align });
}

function renderScaleBar(pdf: jsPDF, element: TemplateElement, scale: MapScale): void {
    const scaleFactor = SCALE_FACTORS[scale];
    const barWidthMm = element.rect.width;

    // Calculate ground distance represented by the bar
    const groundDistanceMeters = (barWidthMm / 1000) * scaleFactor;

    const x = element.rect.x;
    const y = element.rect.y + element.rect.height;

    // White background for visibility
    pdf.setFillColor(255, 255, 255);
    pdf.rect(x - 2, y - element.rect.height - 2, barWidthMm + 30, element.rect.height + 8, 'F');

    // Scale bar line
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(x, y, x + barWidthMm, y);

    // End ticks
    pdf.line(x, y - 3, x, y + 2);
    pdf.line(x + barWidthMm, y - 3, x + barWidthMm, y + 2);
    pdf.line(x + barWidthMm / 2, y - 2, x + barWidthMm / 2, y + 1);

    // Labels
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);

    // Format distance label
    const distanceLabel = groundDistanceMeters >= 1000
        ? `${(groundDistanceMeters / 1000).toFixed(1)} km`
        : `${groundDistanceMeters} m`;

    pdf.text('0', x, y - 5);
    pdf.text(distanceLabel, x + barWidthMm, y - 5);

    // Scale text below bar
    const scaleLabel = scale.replace(':', ' : ');
    pdf.text(scaleLabel, x + barWidthMm / 2, y + 5, { align: 'center' });
}

function renderNorthArrow(pdf: jsPDF, element: TemplateElement): void {
    const x = element.rect.x + element.rect.width / 2;
    const y = element.rect.y;
    const size = Math.min(element.rect.width, element.rect.height);

    // White background circle
    pdf.setFillColor(255, 255, 255);
    pdf.circle(x, y + size / 2, size / 2 + 1, 'F');

    // Arrow body
    pdf.setDrawColor(0, 0, 0);
    pdf.setFillColor(0, 0, 0);
    pdf.setLineWidth(0.3);

    // North arrow triangle (filled)
    const arrowTop = y;
    const arrowBottom = y + size;
    const arrowWidth = size / 3;

    // Left half (filled black)
    pdf.triangle(
        x, arrowTop,           // top
        x - arrowWidth, arrowBottom,  // bottom left
        x, arrowBottom - size / 3,    // center
        'F'
    );

    // Right half (outline only)
    pdf.triangle(
        x, arrowTop,           // top
        x + arrowWidth, arrowBottom,  // bottom right
        x, arrowBottom - size / 3,    // center
        'S'
    );

    // "N" label
    pdf.setFontSize(6);
    pdf.text('N', x, arrowTop - 1, { align: 'center' });
}

function renderLine(pdf: jsPDF, element: TemplateElement): void {
    const style = element.style || {};

    if (style.strokeColor) {
        pdf.setDrawColor(style.strokeColor);
    }
    pdf.setLineWidth(style.strokeWidth || 0.5);

    pdf.line(
        element.rect.x,
        element.rect.y,
        element.rect.x + element.rect.width,
        element.rect.y + element.rect.height
    );
}

/**
 * Saves the PDF and triggers download
 */
export function downloadPdf(pdf: jsPDF, filename?: string): void {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    const name = filename || `map-export-${timestamp}.pdf`;
    pdf.save(name);
}
